import { useEffect, useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import EmailIcon from "@mui/icons-material/Email";
import AddIcon from "@mui/icons-material/Add";

// Firebase
import { app, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { logout } from "../services/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getStorage } from "firebase/storage";

// Context
import { AuthContext } from "../contexts/AuthContext";

// Material-UI
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Dialog,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  RadioGroup,
  Radio,
  FormLabel,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

// Material-UI Icons
import LogoutIcon from "@mui/icons-material/Logout";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CommentIcon from "@mui/icons-material/Comment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Altre librerie
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import { cloneDeep } from "lodash";

// Componenti e utilità personalizzati
import { renderKoToast, renderOkToast } from "../utils/functionUtils";
import CustomAudioPlayer from "../components/AudioPlayer";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import OptimizedImage from "../components/OptimizedImage";

const defaultArticle = {
  title: "",
  content: "",
  image: null,
  audio: null,
  video: null,
  pinned: false,
  published: false,
  position: "start",
};

const defaultCategory = {
  name: "",
};

const defaultComment = {
  comments: [],
};

function Cms() {
  // Context
  const { currentUser } = useContext(AuthContext);

  //articles states
  const [articles, setArticles] = useState([]);
  const [modalArticle, setModalArticle] = useState(defaultArticle);
  const [modalArticleOpen, setModalArticleOpen] = useState(false);
  const [modalArticleEdit, setModalArticleEdit] = useState(false);

  //categories states
  const [categories, setCategories] = useState([]);
  const [modalCategory, setModalCategory] = useState(defaultCategory);
  const [modalCategoryOpen, setModalCategoryOpen] = useState(false);
  const [modalCategoryEdit, setModalCategoryEdit] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Function to get all newsletter subscriber emails in 'Email Nome Cognome' format
  const getNewsletterEmails = () => {
    return newsletterSubscribers
      .map((subscriber) => subscriber.email.trim())
      .join(",");
  };

  //newsletter states
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);

  //comments states
  const [modalComments, setModalComments] = useState(defaultComment);
  const [modalCommentsOpen, setModalCommentsOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);

  const navigate = useNavigate();
  const db = getFirestore(app);
  const storage = getStorage(app);

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link"],
      ["clean"],
    ],
  };

  const articleColumns = [
    {
      field: "published",
      headerName: "Pubblicato",
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}
        </Box>
      ),
    },
    {
      field: "pinned",
      headerName: "Pinnato",
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}
        </Box>
      ),
    },
    { field: "title", headerName: "Titolo", width: 200 },
    { field: "category", headerName: "Categoria", width: 150 },
    {
      field: "date",
      headerName: "Data",
      width: 180,
      renderCell: (params) => {
        const date = dayjs.unix(params.value.seconds);
        return date.format("DD/MM/YYYY");
      },
    },
    {
      field: "description",
      headerName: "Descrizione",
      width: 350,
      flex: 1,
      renderCell: (params) =>
        params.value
          ? params?.value.length > 100
            ? params?.value?.slice(0, 100) + "..."
            : params?.value
          : "-", // Mostra solo una parte della descrizione
    },
    {
      width: 150,
      renderCell: (params) => {
        const hasUncheckedComments =
          params.row.comments?.some((comment) => comment.checked === false) ||
          false;
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <IconButton
              color="primary"
              sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
              onClick={() => handleEditComments(params.row)}
            >
              <CommentIcon fontSize="small" />
              {hasUncheckedComments && (
                <span
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "12px",
                    height: "12px",
                    backgroundColor: "red",
                    borderRadius: "50%",
                  }}
                />
              )}
            </IconButton>
            <IconButton
              color="primary"
              sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
              onClick={() => handleEditArticle(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
              onClick={() => openDeleteArticleConfirmation(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const categoriesColumns = [
    { field: "name", headerName: "Nome", width: 200, flex: 1 },
    {
      width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <IconButton
            color="primary"
            sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
            onClick={() => handleEditCategory(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="primary"
            sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
            onClick={() => openDeleteCategoryConfirmation(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const newsletterColumns = [
    { field: "name", headerName: "Nome", width: 100 },
    { field: "surname", headerName: "Cognome", width: 100 },
    { field: "email", headerName: "Email", width: 400, flex: 1 },
    {
      width: 80,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <IconButton
            color="primary"
            sx={{ "&:hover": { bgcolor: "#e0f7fa" } }}
            onClick={() => openDeleteSubscriberConfirmation(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];
  const getData = useCallback(async () => {
    try {
      // Articles
      let articlesQuery = collection(db, "articles");

      const articleSnapshot = await getDocs(articlesQuery);
      const articlesList = articleSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setArticles(articlesList);

      // Categories
      const categoriesCol = collection(db, "categories");
      const categoriesSnapshot = await getDocs(categoriesCol);
      const categoriesList = categoriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCategories(categoriesList);

      // Newsletter subscribers (solo per admin)
      if (currentUser) {
        const newsletterCol = collection(db, "newsletter");
        const newsletterSnapshot = await getDocs(newsletterCol);
        const newsletterList = newsletterSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNewsletterSubscribers(newsletterList);
      }
    } catch (error) {
      console.error("Errore nel recupero dei dati:", error);
      renderKoToast("Errore nel caricamento dei dati");
    }
  }, [db, currentUser]);

  useEffect(() => {
    //check se use é autenticato
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate("/");
      } else {
        getData();
      }
    });

    return () => unsubscribe();
  }, [navigate, getData]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCloseModal = () => {
    setModalArticle(defaultArticle);

    setModalArticleEdit(false);
    setModalArticleOpen(false);
    setModalCategory({});
    setModalCategoryEdit(false);
    setModalCategoryOpen(false);
    setModalComments({});
    setModalCommentsOpen(false);
  };

  //articles handlers
  const handleArticleChange = (e) => {
    const { name, value, checked } = e.target;

    setModalArticle({
      ...modalArticle,
      [name]:
        e.target.name === "pinned" || e.target.name === "published"
          ? checked
          : value,
    });
  };

  const handleMediaArticleChange = async (type, e) => {
    const article = { ...modalArticle };
    const value = e
      ? type.includes("media")
        ? e.target.files[0]
        : e.target.value
      : null;

    // Se il tipo contiene un punto, gestiamo la notazione puntata
    if (type.includes(".")) {
      const [parent, child] = type.split(".");
      article[parent] = {
        ...article[parent],
        [child]: value,
      };
    } else {
      // Comportamento originale per tipi senza notazione puntata
      article[type] = value;
    }

    setModalArticle(article);
  };

  const handleEditArticle = (item) => {
    setModalArticle(item);
    setModalArticleOpen(true);
    setModalArticleEdit(true);
  };

  const handlePerformEditArticle = async () => {
    if (!currentUser) {
      renderKoToast("Devi essere autenticato per modificare un articolo");
      return;
    }

    const docRef = doc(db, "articles", modalArticle.id);
    let updatedArticle = { ...modalArticle };

    if (
      updatedArticle?.image?.media &&
      typeof updatedArticle.image.media !== "string"
    ) {
      try {
        updatedArticle.image.media = await handleAddMedia("image");
      } catch (error) {
        renderKoToast("Errore nel caricamento dell'immagine");
        return;
      }
    }

    if (
      updatedArticle?.audio?.media &&
      typeof updatedArticle.audio.media !== "string"
    ) {
      try {
        updatedArticle.audio.media = await handleAddMedia("audio");
      } catch (error) {
        renderKoToast("Errore nel caricamento dell'audio");
        return;
      }
    }

    try {
      // Aggiungi i metadati di modifica
      updatedArticle.date = serverTimestamp();
      updatedArticle.updatedAt = serverTimestamp();
      updatedArticle.lastModifiedBy = currentUser.uid;

      await updateDoc(docRef, updatedArticle);
      renderOkToast("Articolo modificato con successo!");
      handleCloseModal();
      getData();
    } catch (err) {
      console.error("Errore durante l'aggiornamento:", err);
      renderKoToast(err.message || "Errore durante la modifica dell'articolo");
    }
  };

  const handleAddMedia = async (type) => {
    if (!currentUser) {
      throw new Error("Utente non autenticato");
    }

    if (!modalArticle[type]?.media) {
      throw new Error(`Nessun file ${type} selezionato`);
    }

    const storeName = type === "audio" ? type : type + "s";
    const fileName = `${Date.now()}-${modalArticle[type].media.name}`;

    const storageRef = ref(storage, `${storeName}/${fileName}`);

    try {
      // Aggiungi metadati al file
      const metadata = {
        contentType: modalArticle[type].media.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString(),
        },
      };

      // Carica il file su Firebase Storage con i metadati
      await uploadBytes(storageRef, modalArticle[type].media, metadata);

      // Ottieni l'URL del file caricato
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      console.error(`Errore nel caricamento del file ${type}:`, error);
      throw new Error(
        `Errore nel caricamento del file ${type}: ${error.message}`
      );
    }
  };

  const handleAddArticle = async () => {
    if (!currentUser) {
      renderKoToast("Devi essere autenticato per aggiungere un articolo");
      return;
    }

    if (
      !modalArticle.title ||
      !modalArticle.category ||
      !modalArticle.description ||
      !modalArticle.body
    ) {
      renderKoToast("Per favore, compila tutti i campi richiesti.");
      return;
    }

    try {
      const newImage = modalArticle?.image?.media
        ? await handleAddMedia("image")
        : null;
      const newAudio = modalArticle?.audio?.media
        ? await handleAddMedia("audio")
        : null;

      // Aggiungi il nuovo articolo al database
      await addDoc(collection(db, "articles"), {
        title: modalArticle.title,
        category: modalArticle.category,
        description: modalArticle.description,
        body: modalArticle.body,
        image: {
          media: newImage,
          position: modalArticle?.position || "start",
        },
        audio: {
          media: newAudio,
          position: modalArticle?.position || "start",
        },
        video: modalArticle?.video || null,
        date: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        lastModifiedBy: currentUser.uid,
        pinned: modalArticle?.pinned || false,
        published: modalArticle?.published || false,
        comments: [],
      });

      renderOkToast("Articolo aggiunto con successo!");
      handleCloseModal();
      getData();
    } catch (e) {
      console.error("Errore durante l'aggiunta dell'articolo:", e);
      renderKoToast(e.message || "Errore durante l'aggiunta dell'articolo");
    }
  };

  const confirmDeleteArticle = async () => {
    if (articleToDelete !== null) {
      try {
        await deleteDoc(doc(db, "articles", articleToDelete));
        setArticles(
          articles.filter((article) => article.id !== articleToDelete)
        );
        renderOkToast("Articolo eliminato con successo!");
        setConfirmDeleteOpen(false);
        setArticleToDelete(null);
      } catch (err) {
        renderKoToast(err);
      }
    }
  };

  //categories handlers
  const handleEditCategory = (item) => {
    setModalCategory(item);
    setModalCategoryOpen(true);
    setModalCategoryEdit(true);
  };

  const handlePerformEditCategory = async () => {
    if (!currentUser) {
      renderKoToast("Devi essere autenticato per modificare una categoria");
      return;
    }

    const docRef = doc(db, "categories", modalCategory.id);
    let updatedCategory = {
      ...modalCategory,
      updatedAt: serverTimestamp(),
      lastModifiedBy: currentUser.uid,
    };

    try {
      await updateDoc(docRef, updatedCategory);
      renderOkToast("Categoria modificata con successo!");
      handleCloseModal();
      getData();
    } catch (err) {
      console.error("Errore durante l'aggiornamento della categoria:", err);
      renderKoToast(
        err.message || "Errore durante la modifica della categoria"
      );
    }
  };

  const handleAddCategory = async () => {
    if (!currentUser) {
      renderKoToast("Devi essere autenticato per aggiungere una categoria");
      return;
    }

    if (!modalCategory.name) {
      renderKoToast("Il nome della categoria è obbligatorio");
      return;
    }

    try {
      await addDoc(collection(db, "categories"), {
        ...modalCategory,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        lastModifiedBy: currentUser.uid,
      });
      renderOkToast("Categoria aggiunta con successo!");
      handleCloseModal();
      getData();
    } catch (e) {
      console.error("Errore durante l'aggiunta della categoria:", e);
      renderKoToast(e.message || "Errore durante l'aggiunta della categoria");
    }
  };

  const confirmDeleteCategory = async () => {
    if (!currentUser) {
      renderKoToast("Devi essere autenticato per eliminare una categoria");
      return;
    }

    if (categoryToDelete !== null) {
      try {
        // Verifica se ci sono articoli che usano questa categoria
        const articlesQuery = query(
          collection(db, "articles"),
          where("category", "==", categoryToDelete)
        );
        const articlesSnapshot = await getDocs(articlesQuery);

        if (!articlesSnapshot.empty) {
          renderKoToast(
            "Impossibile eliminare: esistono articoli che usano questa categoria"
          );
          setConfirmDeleteOpen(false);
          setCategoryToDelete(null);
          return;
        }

        await deleteDoc(doc(db, "categories", categoryToDelete));
        setCategories(
          categories.filter((category) => category.id !== categoryToDelete)
        );
        renderOkToast("Categoria eliminata con successo!");
        setConfirmDeleteOpen(false);
        setCategoryToDelete(null);
      } catch (err) {
        console.error("Errore durante l'eliminazione della categoria:", err);
        renderKoToast(
          err.message || "Errore durante l'eliminazione della categoria"
        );
      }
    }
  };

  //comments handlers
  const handleEditComments = (item) => {
    setModalComments(item);
    setModalCommentsOpen(true);
  };

  const handleChange = (index, field, value) => {
    const updatedComments = cloneDeep(modalComments);
    updatedComments.comments[index].response = value;
    setModalComments(updatedComments);
  };

  const openDeleteCategoryConfirmation = (id) => {
    setCategoryToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const openDeleteSubscriberConfirmation = (id) => {
    setSubscriberToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const openDeleteArticleConfirmation = (id) => {
    setArticleToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const openDeleteConfirmation = (index) => {
    setCommentToDelete(index);
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (commentToDelete !== null) {
      const updatedComments = modalComments.comments.filter(
        (_, i) => i !== commentToDelete
      );
      const docRef = doc(db, "articles", modalComments.id);
      let updatedArticle = { ...modalComments };
      updatedArticle.comments = updatedComments;
      try {
        await updateDoc(docRef, updatedArticle);
        setModalComments(updatedArticle);
        renderOkToast("Commento eliminato con successo!");
        setConfirmDeleteOpen(false);
        setCommentToDelete(null);
      } catch (err) {
        renderKoToast(err);
      }
    }
  };

  const handleSaveCommentResponse = async (index) => {
    const docRef = doc(db, "articles", modalComments.id);
    let updatedArticle = { ...modalComments };
    updatedArticle.comments[index].published =
      !updatedArticle.comments[index].published;
    if (!updatedArticle.comments[index].checked)
      updatedArticle.comments[index].checked = true;

    try {
      await updateDoc(docRef, updatedArticle);
      setModalComments(updatedArticle);
      getData();
      renderOkToast("Commento modificato con successo!");
    } catch (err) {
      renderKoToast(err);
    }
  };

  //newsletter handlers
  const confirmDeleteSubscriber = async () => {
    if (subscriberToDelete !== null) {
      try {
        await deleteDoc(doc(db, "newsletter", subscriberToDelete));
        setNewsletterSubscribers(
          newsletterSubscribers.filter(
            (subscriber) => subscriber.id !== subscriberToDelete
          )
        );
        renderOkToast("Iscritto eliminato con successo!");
        setConfirmDeleteOpen(false);
        setSubscriberToDelete(null);
      } catch (err) {
        renderKoToast(err);
      }
    }
  };

  // Stile per l'input di file nascosto
  const Input = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: "primary.main" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <OptimizedImage
              src={require("../assets/images/newLogoNoBg.png")}
              alt="Admin Logo"
              width={50}
              height={50}
              style={{ marginRight: 10 }}
              loading="eager"
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                display: { xs: "none", sm: "block" }, // Nasconde su mobile
              }}
            >
              Pannello amministratore
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/booking-admin")}
              sx={{ color: "white", borderColor: "white" }}
            >
              Gestione Prenotazioni
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      {/* Tabella articoli */}
      <Box
        sx={{
          mt: 4,
          mb: 2,
          mx: 4,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Articoli
          </Typography>
          {((uncheckedCommentsCount) =>
            uncheckedCommentsCount > 0 && (
              <Chip
                label={`${uncheckedCommentsCount} commento${uncheckedCommentsCount !== 1 ? "i" : ""
                  } da approvare`}
                color="warning"
                size="small"
                sx={{ fontWeight: "bold" }}
              />
            ))(
              articles.flatMap(
                (article) =>
                  article.comments?.filter(
                    (comment) => comment.checked === false
                  ) || []
              ).length
            )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setModalArticleOpen(true)}
          startIcon={<AddIcon />}
        >
          Crea Articolo
        </Button>
      </Box>
      <Box sx={{ height: "auto", mx: 4 }}>
        <DataGrid
          sx={{ bgcolor: "#f0f0f0" }}
          rows={articles}
          columns={articleColumns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
        />

        <Dialog
          open={modalArticleOpen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              handleCloseModal();
            }
          }}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            {modalArticleEdit ? "Modifica articolo" : "Crea un nuovo articolo"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <TextField
                margin="dense"
                label="Titolo"
                type="text"
                fullWidth
                name="title"
                value={modalArticle.title}
                onChange={handleArticleChange}
                variant="outlined"
                required
              />
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  name="category"
                  value={modalArticle.category}
                  onChange={handleArticleChange}
                  variant="outlined"
                  required
                >
                  {categories.map((category, key) => {
                    return (
                      <MenuItem key={key} value={category.name}>
                        {category.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: "100%", position: "relative" }}>
              <TextField
                margin="dense"
                label="Descrizione"
                type="text"
                fullWidth
                name="description"
                value={modalArticle.description}
                onChange={handleArticleChange}
                required
                variant="outlined"
                multiline
                rows={3}
                inputProps={{ maxLength: 150 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    paddingBottom: "25px",
                  },
                }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  position: "absolute",
                  right: "8px",
                  bottom: "8px",
                  backgroundColor: "white",
                  px: 0.5,
                  borderRadius: "4px",
                  pointerEvents: "none",
                }}
              >
                {modalArticle.description?.length || 0}/150 Caratteri
              </Typography>
            </Box>
            <Box sx={{ height: "500px", mt: 1 }}>
              <ReactQuill
                theme="snow"
                value={modalArticle.body}
                onChange={(e) => setModalArticle({ ...modalArticle, body: e })}
                modules={modules}
                style={{ height: "90%" }}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modalArticle.pinned}
                    onChange={handleArticleChange}
                    name="pinned"
                    color="primary"
                  />
                }
                label="Pinnato"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modalArticle.published}
                    onChange={handleArticleChange}
                    name="published"
                    color="primary"
                  />
                }
                label="Pubblicato"
              />
            </Box>
            <div>
              <Typography variant="h6" gutterBottom>
                Media
              </Typography>

              {/* Accordion per Immagini */}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography>Immagine</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {modalArticle && modalArticle?.image?.media ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={2}
                    >
                      <OptimizedImage
                        src={modalArticle?.image?.media}
                        alt="Anteprima immagine"
                        width={300}
                        height={200}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: 4,
                          boxShadow: 1,
                        }}
                        loading="eager"
                      />
                      <div>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          component="label"
                        >
                          Modifica
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              handleMediaArticleChange("image.media", e)
                            }
                          />
                        </Button>
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            handleMediaArticleChange("image.media", null)
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                      <div>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Posizione</FormLabel>
                          <RadioGroup
                            aria-label="posizione"
                            name="posizione"
                            value={modalArticle?.image?.position}
                            onChange={(e) =>
                              handleMediaArticleChange("image.position", e)
                            }
                            sx={{
                              flexDirection: "row",
                            }}
                          >
                            <FormControlLabel
                              value="start"
                              control={<Radio />}
                              label="Inizio"
                            />
                            <FormControlLabel
                              value="end"
                              control={<Radio />}
                              label="Fine"
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </Box>
                  ) : (
                    <Button variant="contained" component="label">
                      Carica Immagine
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleMediaArticleChange("image.media", e)
                        }
                      />
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Accordion per Audio */}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2a-content"
                  id="panel2a-header"
                >
                  <Typography>Audio</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {modalArticle && modalArticle?.audio?.media ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={2}
                      width={"auto"}
                    >
                      <Box width={1}>
                        <CustomAudioPlayer
                          audioURL={modalArticle.audio?.media}
                        />
                      </Box>
                      <div>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          component="label"
                        >
                          Modifica
                          <Input
                            type="file"
                            accept="audio/mpeg"
                            onChange={(e) =>
                              handleMediaArticleChange("audio.media", e)
                            }
                          />
                        </Button>
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            handleMediaArticleChange("audio.media", null)
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                      <div>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Posizione</FormLabel>
                          <RadioGroup
                            aria-label="posizione"
                            name="posizione"
                            value={modalArticle?.audio?.position}
                            onChange={(e) =>
                              handleMediaArticleChange("audio.position", e)
                            }
                            sx={{
                              flexDirection: "row", // Dispone gli elementi in orizzontale
                            }}
                          >
                            <FormControlLabel
                              value="start"
                              control={<Radio />}
                              label="Inizio"
                            />
                            <FormControlLabel
                              value="end"
                              control={<Radio />}
                              label="Fine"
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </Box>
                  ) : (
                    <Button variant="contained" component="label">
                      Carica Audio
                      <Input
                        type="file"
                        accept="audio/mpeg"
                        onChange={(e) =>
                          handleMediaArticleChange("audio.media", e)
                        }
                      />
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Accordion per Video */}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3a-content"
                  id="panel3a-header"
                >
                  <Typography>Video (Link YouTube)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    margin="dense"
                    label="Video"
                    type="text"
                    fullWidth
                    name="video"
                    value={modalArticle.video}
                    onChange={handleArticleChange}
                    variant="outlined"
                  />
                </AccordionDetails>
              </Accordion>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Annulla
            </Button>
            <Button
              onClick={
                modalArticleEdit ? handlePerformEditArticle : handleAddArticle
              }
              color="primary"
            >
              Salva
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={modalCommentsOpen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              handleCloseModal();
            }
          }}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>{"Commenti"}</DialogTitle>
          <DialogContent>
            <Box>
              {modalComments.comments && modalComments.comments.length > 0 ? (
                modalComments.comments
                  .sort((a, b) => b.timestamp.seconds - a.timestamp.seconds)
                  .map((comment, index) => (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ flexGrow: 1 }}>
                          <span className="fw-bold">
                            {comment.firstName} {comment.lastName}
                          </span>{" "}
                          ha commentato il{" "}
                          {dayjs(comment.timestamp?.seconds * 1000).format(
                            "DD/MM/YYYY HH:mm"
                          )}
                        </Typography>
                        <Typography
                          color={
                            comment.published ? "success.main" : "warning.main"
                          }
                        >
                          {comment.published
                            ? "Pubblicato"
                            : comment.checked
                              ? "Archiviato"
                              : "Da Approvare"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography sx={{ mb: 1 }}>{comment.text}</Typography>
                        <TextField
                          label="Risposta"
                          disabled={comment.published}
                          multiline
                          fullWidth
                          rows={3}
                          value={comment.response || ""}
                          onChange={(e) =>
                            handleChange(index, "response", e.target.value)
                          }
                        />
                        <Box display={"flex"} gap={2}>
                          <Button
                            onClick={() => handleSaveCommentResponse(index)}
                            sx={{ mt: 2 }}
                            variant="contained"
                            color={comment.published ? "warning" : "primary"}
                          >
                            {comment.published
                              ? "Archivia"
                              : "Salva e Pubblica"}
                          </Button>
                          <Button
                            onClick={() => openDeleteConfirmation(index)}
                            sx={{ mt: 2 }}
                            variant="contained"
                            color="error"
                          >
                            Elimina
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))
              ) : (
                <Typography>Nessun commento inserito</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      {/* Tabelle categorie e iscritti alla newsletter */}
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box sx={{ height: "600px", mx: 4, width: "45vw" }}>
          <Box
            sx={{
              mt: 4,
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Categorie
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModalCategoryOpen(true)}
              startIcon={<AddIcon />}
            >
              Crea Categoria
            </Button>
          </Box>
          <DataGrid
            sx={{ bgcolor: "#f0f0f0" }}
            rows={categories}
            columns={categoriesColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 8 } },
            }}
            pageSizeOptions={[8]}
            disableRowSelectionOnClick
            autoHeight
          />
          <Dialog
            open={modalCategoryOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                handleCloseModal();
              }
            }}
          >
            <DialogTitle>
              {modalCategoryEdit
                ? "Modifica categoria"
                : "Crea una nuova categoria"}
            </DialogTitle>
            <DialogContent>
              <TextField
                margin="dense"
                label="Nome"
                type="text"
                fullWidth
                name="name"
                value={modalCategory.name}
                onChange={(event) =>
                  setModalCategory({
                    ...modalCategory,
                    name: event.target.value,
                  })
                }
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} color="primary">
                Annulla
              </Button>
              <Button
                onClick={
                  modalCategoryEdit
                    ? handlePerformEditCategory
                    : handleAddCategory
                }
                color="primary"
              >
                Salva
              </Button>
            </DialogActions>
          </Dialog>
          <ConfirmDeleteModal
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={
              articleToDelete !== null
                ? confirmDeleteArticle
                : categoryToDelete !== null
                  ? confirmDeleteCategory
                  : subscriberToDelete !== null
                    ? confirmDeleteSubscriber
                    : confirmDeleteComment
            }
          />
        </Box>

        <Box sx={{ height: "600px", mx: 4, width: "45vw" }}>
          <Box
            sx={{
              mt: 4,
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Iscritti alla Newsletter
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const emails = getNewsletterEmails();
                window.location.href = `mailto:ilariastumpo.coaching@gmail.com?bcc=${emails}`;
              }}
              startIcon={<EmailIcon />}
            >
              Invia email
            </Button>
          </Box>
          <DataGrid
            sx={{ bgcolor: "#f0f0f0", width: "100%", height: "auto" }}
            rows={newsletterSubscribers}
            columns={newsletterColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 8 } },
            }}
            pageSizeOptions={[8]}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      </Box>
    </>
  );
}

export default Cms;
